import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  SlidersHorizontal,
  Download,
  Copy,
} from "lucide-react";
import { listUsageAndConnections, type CurrentUser } from "../../lib/api";

const DEMO_EXPIRY_DATA = [
  { id: "exp-1", tenant: "Interlink Consultant", phone: "919323818985", user: "ariham drea", user_phone: "917021977790", instance: "Default instance", quota: "4706", expiry: "02/09/2026", connection: "Disconnected", webhook: "Disabled", created_at: "23/07/2025" },
  { id: "exp-2", tenant: "Interlink Consultant", phone: "919323818985", user: "aristo", user_phone: "919326684839", instance: "Default instance", quota: "3943", expiry: "16/09/2026", connection: "Disconnected", webhook: "Disabled", created_at: "10/09/2025" },
  { id: "exp-3", tenant: "Interlink Consultant", phone: "919323818985", user: "shagufta garments", user_phone: "919326853990", instance: "Default instance", quota: "10478", expiry: "19/09/2026", connection: "Disconnected", webhook: "Disabled", created_at: "08/05/2024" },
  { id: "exp-4", tenant: "Interlink Consultant", phone: "919323818985", user: "nmt", user_phone: "919769580202", instance: "Default instance", quota: "3891", expiry: "17/09/2026", connection: "Disconnected", webhook: "Disabled", created_at: "08/05/2024" },
  { id: "exp-5", tenant: "Interlink Consultant", phone: "919323818985", user: "Raj Kamal", user_phone: "919137014146", instance: "Default instance", quota: "6877", expiry: "27/09/2026", connection: "Disconnected", webhook: "Disabled", created_at: "29/06/2024" },
  { id: "exp-6", tenant: "Interlink Consultant", phone: "919323818985", user: "twix", user_phone: "918454999364", instance: "Default instance", quota: "0", expiry: "12/09/2026", connection: "Disconnected", webhook: "Disabled", created_at: "31/05/2024" },
];

const DEMO_CONNECTION_ACTIVITY = [
  { id: "ca-1", user: "aarti", phone: "919870759951", tenant: "Interlink Consultant", instance: "aarti's instance", key: "v7uyogpvm6n0act523k7vj5y", event: "Login", detail: "919870759951", timestamp: "2026-08-27 15:46", reason: "Logged Out From Panel" },
  { id: "ca-2", user: "aarti", phone: "919870759951", tenant: "Interlink Consultant", instance: "aarti's instance", key: "v7uyogpvm6n0act523k7vj5y", event: "Logout", detail: "NA", timestamp: "2026-08-27 15:45", reason: "Logged out from another device" },
  { id: "ca-3", user: "aarti", phone: "919870759951", tenant: "Interlink Consultant", instance: "aarti's instance", key: "v7uyogpvm6n0act523k7vj5y", event: "Login", detail: "919870759951", timestamp: "2026-08-27 15:44", reason: "Logged Out From Panel" },
  { id: "ca-4", user: "aarti", phone: "919870759951", tenant: "Interlink Consultant", instance: "aarti's instance", key: "v7uyogpvm6n0act523k7vj5y", event: "Logout", detail: "NA", timestamp: "2026-08-25 12:39", reason: "Account restricted/banned" },
  { id: "ca-5", user: "RDGOHEL", phone: "917900135178", tenant: "Interlink Consultant", instance: "Default instance", key: "clvyterfyxfqbm51eu3vmvisc", event: "Login", detail: "R.D.Gohel 917900135178", timestamp: "2026-08-19 14:00", reason: "Logged Out From Panel" },
];

export function UsageConnectionReportsView({}: { token: string; user: CurrentUser }) {
  const [activeTab, setActiveTab] = useState<"usage" | "expiry" | "connections">("usage");
  const [searchQuery, setSearchQuery] = useState("");

  const reportsQuery = useQuery({
    queryKey: ["usage-and-connections"],
    queryFn: () => listUsageAndConnections(),
    refetchInterval: 10_000,
  });

  const usageData = reportsQuery.data?.usage || [];

  function copyText(text: string) {
    navigator.clipboard.writeText(text);
  }

  return (
    <div className="space-y-5">
      {/* Header & Tabs */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
            {activeTab === "usage"
              ? "Today's Usage"
              : activeTab === "expiry"
              ? "Expiry Report"
              : "Connection Report"}
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            {activeTab === "usage"
              ? "Monitor and manage today's usage instances across all organizations and users."
              : activeTab === "expiry"
              ? "View and manage instance expiry information"
              : "View connection login/logout activity."}
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center p-1 rounded-lg bg-slate-100 text-xs">
          <button
            onClick={() => setActiveTab("usage")}
            className={`px-3 py-1.5 rounded-md font-semibold transition-all ${
              activeTab === "usage" ? "bg-white text-emerald-600 shadow-2xs" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Today's Usage
          </button>
          <button
            onClick={() => setActiveTab("expiry")}
            className={`px-3 py-1.5 rounded-md font-semibold transition-all ${
              activeTab === "expiry" ? "bg-white text-emerald-600 shadow-2xs" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Expiry Report
          </button>
          <button
            onClick={() => setActiveTab("connections")}
            className={`px-3 py-1.5 rounded-md font-semibold transition-all ${
              activeTab === "connections" ? "bg-white text-emerald-600 shadow-2xs" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Connection Activity
          </button>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-64">
            <input
              type="text"
              placeholder="Search instances..."
              className="modern-input w-full text-xs py-2"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {activeTab === "expiry" && (
            <>
              <button className="filter-chip">⊕ Connection Status</button>
              <button className="filter-chip">⊕ Organization</button>
              <button className="filter-chip">⊕ User</button>
              <button className="filter-chip">⊕ Credits Range</button>
            </>
          )}

          {activeTab === "connections" && (
            <>
              <button className="filter-chip">⊕ User</button>
              <button className="filter-chip">⊕ Connection</button>
              <button className="filter-chip">⊕ Events</button>
              <button className="filter-chip">⊕ Date range</button>
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          {activeTab !== "usage" && (
            <button className="btn-secondary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs">
              <Download className="w-3.5 h-3.5 text-slate-500" />
              <span>Export</span>
            </button>
          )}
          <button className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs">
            <SlidersHorizontal className="w-3.5 h-3.5 text-slate-500" />
            <span>View</span>
          </button>
        </div>
      </div>

      {/* Tab 1: Today's Usage (Exact Matches 06_today_usage.png) */}
      {activeTab === "usage" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
          <div className="overflow-x-auto">
            <table className="modern-table">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>User</th>
                  <th>Instance</th>
                  <th>Connection</th>
                  <th>Today Quota Usage</th>
                </tr>
              </thead>
              <tbody>
                {usageData.map((u, idx) => (
                  <tr key={idx} className="hover:bg-slate-50/70 transition-colors">
                    <td>
                      <div className="font-semibold text-slate-800 text-xs">Interlink Consultant</div>
                      <div className="font-mono text-[11px] text-slate-500">919323818985</div>
                    </td>
                    <td>
                      <div className="font-bold text-slate-900 text-xs">{u.user_name}</div>
                      <div className="font-mono text-[11px] text-slate-500">918454818800</div>
                    </td>
                    <td className="font-medium text-xs text-slate-700">{u.instance_name}</td>
                    <td>
                      <span className="status-pill status-pill--connected">Connected</span>
                    </td>
                    <td className="font-mono text-xs font-bold text-slate-900">{u.sent_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab 2: Expiry Report (Exact Matches 08_expiry_report.png) */}
      {activeTab === "expiry" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
          <div className="overflow-x-auto">
            <table className="modern-table">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>User</th>
                  <th>Instance Name</th>
                  <th>Instance Quota ↕</th>
                  <th>Expiry Date ↕</th>
                  <th>Connection</th>
                  <th>Webhook</th>
                  <th>Created At</th>
                </tr>
              </thead>
              <tbody>
                {DEMO_EXPIRY_DATA.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/70 transition-colors">
                    <td>
                      <div className="font-semibold text-slate-800 text-xs">{item.tenant}</div>
                      <div className="font-mono text-[11px] text-slate-500">{item.phone}</div>
                    </td>
                    <td>
                      <div className="font-bold text-slate-900 text-xs">{item.user}</div>
                      <div className="font-mono text-[11px] text-slate-500">{item.user_phone}</div>
                    </td>
                    <td className="font-medium text-xs text-slate-700">{item.instance}</td>
                    <td className="font-mono text-xs font-bold text-slate-800">{item.quota}</td>
                    <td className="font-mono text-xs text-slate-600">{item.expiry}</td>
                    <td>
                      <span className="status-pill status-pill--disconnected">{item.connection}</span>
                    </td>
                    <td>
                      <span className="text-xs text-slate-500 font-medium">{item.webhook}</span>
                    </td>
                    <td className="font-mono text-xs text-slate-500">{item.created_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab 3: Connection Activity (Exact Matches 09_connection_report.png) */}
      {activeTab === "connections" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
          <div className="overflow-x-auto">
            <table className="modern-table">
              <thead>
                <tr>
                  <th>User ↕</th>
                  <th>Organization ↕</th>
                  <th>Instance Name</th>
                  <th>Events ↕</th>
                  <th>Connection Detail</th>
                  <th>Timestamp ↕</th>
                  <th>Reason ↕</th>
                </tr>
              </thead>
              <tbody>
                {DEMO_CONNECTION_ACTIVITY.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/70 transition-colors">
                    <td>
                      <div className="font-bold text-slate-900 text-xs">{item.user}</div>
                      <div className="font-mono text-[11px] text-slate-500">{item.phone}</div>
                    </td>
                    <td>
                      <div className="font-semibold text-slate-800 text-xs">{item.tenant}</div>
                      <div className="font-mono text-[11px] text-slate-500">919323818985</div>
                    </td>
                    <td>
                      <div className="font-semibold text-slate-900 text-xs">{item.instance}</div>
                      <div className="flex items-center gap-1 text-[11px] font-mono text-slate-500">
                        <button onClick={() => copyText(item.key)} className="hover:text-slate-900">
                          <Copy className="w-3 h-3 text-slate-400" />
                        </button>
                        <span>{item.key.slice(0, 14)}...</span>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`status-pill ${
                          item.event === "Login" ? "status-pill--connected" : "status-pill--disconnected"
                        }`}
                      >
                        {item.event}
                      </span>
                    </td>
                    <td className="font-mono text-xs text-slate-700">{item.detail}</td>
                    <td className="font-mono text-xs text-slate-600">{item.timestamp}</td>
                    <td className="text-xs text-slate-600">{item.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
